import { StudyEventDto } from '../../models/study-next.model';

/**
 * What one realtime event asks the page to re-read. `rows` is the scope's metadata then a grid re-query; `units`
 * are updated in place when on screen; `performanceFilters` are the active subcategory's, nothing reads another's.
 */
export interface StudyRefresh {
  /** With `rows`, the re-query waits for the detail too: a working condition's line lands before its rows. */
  detail: boolean;
  counters: boolean;
  goodForSelection: boolean;
  rows: boolean;
  /**
   * A re-query of the grid alone: which units a page holds changed, not what they are projected against,
   * so nothing has to land first. `rows` implies it.
   */
  requery: boolean;
  units: string[];
  performanceFilters: boolean;
  /**
   * Whether the event can change the metadata of the scopes it names — its subcategory and All models.
   * The page holds the metadata of the tabs visited before, and drops those copies on it.
   */
  metadata: boolean;
}

const NOTHING: StudyRefresh = {
  detail: false,
  counters: false,
  goodForSelection: false,
  rows: false,
  requery: false,
  units: [],
  performanceFilters: false,
  metadata: false,
};

/** Only the rows depend on the scope: the counters label every tab, and the tab strip shows on all of them. */
function inScope(event: StudyEventDto, activeSubcategoryId: string | null): boolean {
  return event.subcategoryId == null || activeSubcategoryId === null || event.subcategoryId === activeSubcategoryId;
}

/**
 * The event → refresh table. `activeSubcategoryId` is the store's `subcategoryId()`, `null` on All models.
 * `STUDY_DELETED` is not a refresh — the page leaves — and asks for nothing here. The `switch` is exhaustive
 * on the contract's enum — a new event type fails to compile until this table says what it means — and its
 * `default` keeps a client older than the backend alive: an unknown type re-reads everything.
 */
export function planRefresh(event: StudyEventDto, activeSubcategoryId: string | null): StudyRefresh {
  const rows = inScope(event, activeSubcategoryId);

  switch (event.type) {
    case 'STUDY_CREATED':
      // Another study: it concerns the project's study list, never this page.
      return { ...NOTHING };
    case 'STUDY_UPDATED':
      return { ...NOTHING, detail: true };
    case 'STUDY_CURRENCY_CHANGED':
      return { ...NOTHING, detail: true, rows: true };
    case 'STUDY_DELETED':
      return { ...NOTHING };
    case 'SUBCATEGORY_ADDED':
    case 'SUBCATEGORY_REMOVED':
    case 'WORKING_CONDITION_ADDED':
    case 'WORKING_CONDITION_UPDATED':
    case 'WORKING_CONDITION_REMOVED':
      return { ...NOTHING, detail: true, counters: true, goodForSelection: true, rows, metadata: true };
    case 'UNITS_ADDED':
    case 'UNITS_REMOVED':
      // Which subcategories are tabs follows the unit set, and so does `canAddExternalUnits` (a subclass-5 unit
      // forbids external units): the detail, whatever the tab.
      return { ...NOTHING, detail: true, counters: true, goodForSelection: true, rows, metadata: true };
    case 'PERFORMANCES_RECALCULATED':
      // Nothing in the detail follows the performances.
      return { ...NOTHING, counters: true, goodForSelection: true, rows, metadata: true };
    case 'UNITS_UPDATED':
      return {
        ...NOTHING,
        counters: true,
        goodForSelection: true,
        units: rows ? (event.unitIds ?? []) : [],
        metadata: true,
      };
    case 'PERFORMANCE_FILTER_CHANGED':
      // Persisted filters change which units a page holds and the counters, `hasFilters` included — not the
      // metadata (it counts every unit), the detail, or good-for-selection (its query ignores them).
      return {
        ...NOTHING,
        counters: true,
        requery: rows,
        performanceFilters: event.subcategoryId != null && event.subcategoryId === activeSubcategoryId,
      };
    default: {
      // Compile-time: a type added to the contract must be given a row above, or this assignment fails.
      // Runtime: a bundle older than the backend still receives it — something changed, re-read everything.
      const unknown: never = event.type;
      console.warn('[study-next] unknown study event type, re-reading everything', unknown);
      return { ...NOTHING, detail: true, counters: true, goodForSelection: true, rows: true, metadata: true };
    }
  }
}

/** Two plans as one, for events that arrive together: every re-read either asks for, each unit once. */
export function mergeRefresh(a: StudyRefresh, b: StudyRefresh): StudyRefresh {
  return {
    detail: a.detail || b.detail,
    counters: a.counters || b.counters,
    goodForSelection: a.goodForSelection || b.goodForSelection,
    rows: a.rows || b.rows,
    requery: a.requery || b.requery,
    units: [...new Set([...a.units, ...b.units])],
    performanceFilters: a.performanceFilters || b.performanceFilters,
    metadata: a.metadata || b.metadata,
  };
}
